import { BookingFlight, BookingStop } from "@/redux/slices/booking/types";

/** Only the shape these two read, so a caller with a narrower stop type still fits. */
type FlightBearingStop = Pick<BookingStop, "flight">;

/**
 * Which stop carries which flight (DEV-1487).
 *
 * A job can now carry two: the ARRIVAL flight on the pickup, and the DEPARTURE flight on the drop-off.
 * The provider payload puts each in that stop's own `flight` slot — `stops[0]` and `stops[last]` — so
 * the leg is decided by POSITION, and the flight object itself carries nothing that names its leg.
 *
 * These exist because the surfaces used to search `stops.find((stop) => stop.flight)` instead, which
 * answers "any flight anywhere on this job". That was harmless while only the pickup could carry one,
 * and became wrong the moment the drop-off could: on a departure booking the search found QF499 on the
 * last stop and every caller then treated it as the arrival flight — hanging its chip on the pickup
 * cell (staging J000527, where the chip sat under Accor Stadium), and reading its terminal off the
 * `arrival` leg, which is the terminal at the far end of the route.
 */
export const arrivalFlightOf = (stops: FlightBearingStop[] | undefined): BookingFlight | null => stops?.[0]?.flight ?? null;

/**
 * The DEPARTURE flight, off the last stop.
 *
 * `length > 1` mirrors the backend's own guard (`$lastStopKey !== 0` in `JobEntityMinimalHydrator`):
 * on a one-stop job the first and last stop are the same row, and reading it here would report the
 * ARRIVAL flight as a departure and draw the chip twice.
 */
export const departureFlightOf = (stops: FlightBearingStop[] | undefined): BookingFlight | null =>
  stops && stops.length > 1 ? stops[stops.length - 1]?.flight ?? null : null;
